// Lokal gemning under nøglen fs:v1. Alt er pakket i try/catch: privat vindue eller blokeret
// lager må aldrig vælte spillet, så vi falder tilbage til en tom profil i hukommelsen.

import { nextStreak } from './content/daily';
import type { DailyStreak } from './content/daily';
import type { DifficultyId } from './sim';

export interface Save {
  v: 1;
  difficulty: DifficultyId;
  /** Bedste stjerner pr. bane og sværhed: `${level}:${difficulty}`. */
  stars: Record<string, number>;
  muted: boolean;
  speed: 1 | 2;
  /** Lydstyrker 0..1. Mangler de, bruges standarden. */
  vol?: { master: number; music: number; sfx: number };
  /** Grafik: auto skifter selv efter billedraten. */
  quality?: QualityMode;
  /** Bevægelse: auto følger systemets prefers-reduced-motion. */
  motion?: MotionMode;
  /** Vibration ved byggeri, brud og mørke (kun hvor telefonen kan). Mangler den, er den til. */
  haptics?: boolean;
  /** Hvilke af hånd-lektionerne spilleren har klaret. */
  coach?: Partial<Record<Lesson, boolean>>;
  /** Låste-op Netatlas-kort: `${level}#${i}`. */
  atlas?: string[];
  /** Spillerens rolle, når Volt spiller med (Volt tager den anden). */
  voltRole?: 'planner' | 'drift';
  /** Dagens vagt: bedste resultat pr. dato (YYYY-MM-DD). */
  daily?: Record<string, DailyRecord>;
  /** Dagens vagt: dage i træk med en spillet vagt. */
  dailyStreak?: DailyStreak;
  /** Tips-turen er set eller sprunget over (vises kun første gang). */
  tips?: boolean;
}

export interface DailyRecord {
  stars: number;
  won: boolean;
  /** Mørklagte byer pr. spillet år (til gitteret). */
  marks: number[];
  /** Grøn strøm i procent (til sammenligning med dagens par). */
  green?: number;
}

export type QualityMode = 'auto' | 'low' | 'mid' | 'high';
export type MotionMode = 'auto' | 'calm' | 'full';
export type Lesson = 'drag' | 'upgrade' | 'reinforce' | 'card' | 'power';

export const DEFAULT_VOL = { master: 0.8, music: 0.5, sfx: 0.8 };

const KEY = 'fs:v1';

function empty(): Save {
  return { v: 1, difficulty: 'laerling', stars: {}, muted: false, speed: 1 };
}

let cache: Save | null = null;

export function load(): Save {
  if (cache) return cache;
  let s = empty();
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Save>;
      if (parsed && parsed.v === 1) s = { ...s, ...parsed, stars: { ...(parsed.stars ?? {}) } };
    }
  } catch {
    // Ingen adgang til lageret: brug standardprofilen.
  }
  cache = s;
  return s;
}

export function save(patch: Partial<Save>): Save {
  const s = { ...load(), ...patch };
  cache = s;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Lageret er fuldt eller blokeret; spillet kører videre uden.
  }
  return s;
}

export function bestStars(level: string, diff: DifficultyId): number {
  return load().stars[`${level}:${diff}`] ?? 0;
}

export function recordStars(level: string, diff: DifficultyId, stars: number): void {
  const s = load();
  const key = `${level}:${diff}`;
  if ((s.stars[key] ?? 0) >= stars) return;
  save({ stars: { ...s.stars, [key]: stars } });
}

/** Bedste stjerner på banen for hver sværhed. */
export function starsByDifficulty(level: string): Partial<Record<DifficultyId, number>> {
  const out: Partial<Record<DifficultyId, number>> = {};
  for (const [k, n] of Object.entries(load().stars)) {
    const [lv, d] = k.split(':');
    if (lv === level && d && n > 0) out[d as DifficultyId] = n;
  }
  return out;
}

/** Banen er vundet på mindst én sværhed. */
export function levelWon(level: string): boolean {
  return Object.keys(starsByDifficulty(level)).length > 0;
}

/** Alle stjerner på alle baner og sværheder. */
export function totalStars(): number {
  return Object.values(load().stars).reduce((a, n) => a + (n > 0 ? n : 0), 0);
}

export function dailyRecord(key: string): DailyRecord | null {
  return load().daily?.[key] ?? null;
}

/** Gem dagens resultat, hvis det er bedre end det, der står. Ældre dage end de sidste 30 ryddes. */
export function recordDaily(key: string, r: DailyRecord): void {
  // Stimen tæller alle spillede vagter, også dem der ikke slår dagens bedste.
  const streak = nextStreak(load().dailyStreak, key);
  const all = { ...(load().daily ?? {}) };
  const old = all[key];
  if (old && ((old.won && !r.won) || old.stars > r.stars || (old.stars === r.stars && (old.green ?? 0) >= (r.green ?? 0)))) {
    save({ dailyStreak: streak });
    return;
  }
  all[key] = r;
  const keys = Object.keys(all).sort();
  for (const k of keys.slice(0, Math.max(0, keys.length - 30))) delete all[k];
  save({ daily: all, dailyStreak: streak });
}

export function dailyStreak(): DailyStreak | null {
  return load().dailyStreak ?? null;
}

export function volumes(): { master: number; music: number; sfx: number } {
  return { ...DEFAULT_VOL, ...(load().vol ?? {}) };
}

export function lessonDone(l: Lesson): boolean {
  return !!load().coach?.[l];
}

export function markLesson(l: Lesson, done = true): void {
  save({ coach: { ...(load().coach ?? {}), [l]: done } });
}

export function resetLessons(): void {
  save({ coach: {} });
}

export function atlasUnlocked(): Set<string> {
  return new Set(load().atlas ?? []);
}

/** Lås et Netatlas-kort op. Svarer true, hvis kortet var nyt. */
export function unlockAtlas(id: string): boolean {
  const have = load().atlas ?? [];
  if (have.includes(id)) return false;
  save({ atlas: [...have, id] });
  return true;
}
